import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
    User, 
    Heart, 
    MapPin,
    Clock,
    Users,
    ArrowRight,
    CheckCircle,
    Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { 
    validateSetupWizardForm, 
    sanitizeSetupWizardForm 
} from '../utils/inputSanitization';

const SetupWizard = () => {
    const { user, userProfile, refreshUserProfile } = useAuth();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const totalSteps = 5;
    const [formData, setFormData] = useState({
        // Basic Info
        display_name: userProfile?.name || '',
        
        // Activity Preferences
        activityPreferences: [],
        customActivities: '',
        
        // Dietary Restrictions
        dietaryRestrictions: [],
        customDietary: '',
        
        // Location Preferences
        travelRadius: 'medium', // small, medium, large
        preferredAreas: [],
        customAreas: '',
        
        // Budget Preferences
        budgetRange: 'medium', // low, medium, high, luxury
        
        // Time Preferences
        preferredTimes: [],
        dayPreferences: [],
        
        // Group Preferences
        comfortableGroupSize: 'medium', // small, medium, large
    });

    // Predefined options
    const activityOptions = [
        { name: 'Drinking & Nightlife', icon: '🍻' },
        { name: 'Dining & Food', icon: '🍽️' },
        { name: 'Outdoor Activities', icon: '🌲' },
        { name: 'Cultural Events', icon: '🎭' },
        { name: 'Sports & Fitness', icon: '⚽' },
        { name: 'Entertainment', icon: '🎬' },
        { name: 'Shopping', icon: '🛍️' },
        { name: 'Wellness & Spa', icon: '🧘' },
        { name: 'Educational', icon: '📚' },
        { name: 'Adventure', icon: '🏔️' },
        { name: 'Relaxation', icon: '🏖️' },
        { name: 'Social Events', icon: '🎉' }
    ];

    const dietaryOptions = [
        { name: 'Vegetarian', icon: '🥬' },
        { name: 'Vegan', icon: '🌱' },
        { name: 'Halal', icon: '🕌' },
        { name: 'Kosher', icon: '✡️' },
        { name: 'Gluten-Free', icon: '🌾' },
        { name: 'Dairy-Free', icon: '🥛' },
        { name: 'Nut-Free', icon: '🥜' },
        { name: 'Seafood-Free', icon: '🐟' },
        { name: 'Low-Carb', icon: '🥗' },
        { name: 'Low-Sodium', icon: '🧂' },
        { name: 'Organic', icon: '🌿' }
    ];

    const timeOptions = [
        { name: 'Early Morning', time: '6-9 AM', icon: '🌅' },
        { name: 'Morning', time: '9-12 PM', icon: '☀️' },
        { name: 'Afternoon', time: '12-5 PM', icon: '🌤️' },
        { name: 'Evening', time: '5-8 PM', icon: '🌆' },
        { name: 'Night', time: '8-11 PM', icon: '🌃' },
        { name: 'Late Night', time: '11 PM+', icon: '🌙' }
    ];

    const dayOptions = [
        { name: 'Monday', short: 'Mon', icon: '📅' },
        { name: 'Tuesday', short: 'Tue', icon: '📅' },
        { name: 'Wednesday', short: 'Wed', icon: '📅' },
        { name: 'Thursday', short: 'Thu', icon: '📅' },
        { name: 'Friday', short: 'Fri', icon: '📅' },
        { name: 'Saturday', short: 'Sat', icon: '📅' },
        { name: 'Sunday', short: 'Sun', icon: '📅' }
    ];

    const radiusOptions = [
        { value: 'small', label: 'Local (0-5 miles)', description: 'Stay close to home' },
        { value: 'medium', label: 'Regional (5-20 miles)', description: 'Explore your area' },
        { value: 'large', label: 'Extended (20+ miles)', description: 'Willing to travel far' }
    ];

    const budgetOptions = [
        { value: 'low', label: 'Budget-Friendly ($)', description: 'Affordable options' },
        { value: 'medium', label: 'Standard ($$)', description: 'Moderate pricing' },
        { value: 'high', label: 'Premium ($$$)', description: 'Higher-end experiences' },
        { value: 'luxury', label: 'Luxury ($$$$)', description: 'Top-tier experiences' }
    ];

    const groupSizeOptions = [
        { value: 'small', label: 'Small (2-4 people)', description: 'Intimate gatherings' },
        { value: 'medium', label: 'Medium (5-8 people)', description: 'Group activities' },
        { value: 'large', label: 'Large (9+ people)', description: 'Big group events' }
    ];

    // Form handlers
    const handleInputChange = (field, value) => {
        // Sanitize input based on field type
        let sanitizedValue = value;
        
        if (typeof value === 'string') {
            switch (field) {
                case 'display_name':
                    sanitizedValue = value.trim().substring(0, 100);
                    break;
                case 'customActivities':
                case 'customDietary':
                case 'customAreas':
                    sanitizedValue = value.trim().substring(0, 500);
                    break;
                default:
                    sanitizedValue = value.trim();
            }
        }
        
        setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    };

    const handleArrayToggle = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter(item => item !== value)
                : [...prev[field], value]
        }));
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            console.log('🚀 Submitting setup data...', formData);
            
            // Validate form data
            const validation = validateSetupWizardForm(formData);
            if (!validation.isValid) {
                const errorMessages = validation.errors.map(err => `${err.field}: ${err.message}`).join(', ');
                toast.error(`Validation failed: ${errorMessages}`);
                setLoading(false);
                return;
            }
            
            // Sanitize form data
            const sanitizedData = sanitizeSetupWizardForm(formData);
            
            // Prepare data for backend
            const submitData = {
                ...sanitizedData,
                is_setup_complete: true,
                preferences: [
                    ...sanitizedData.activityPreferences,
                    ...(sanitizedData.customActivities ? [sanitizedData.customActivities] : [])
                ],
                dietary_restrictions: [
                    ...sanitizedData.dietaryRestrictions,
                    ...(sanitizedData.customDietary ? [sanitizedData.customDietary] : [])
                ]
            };
            
            console.log('📤 Sending sanitized data to backend:', submitData);

            // Send to backend
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/users/${userProfile.user_id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submitData)
            });

            console.log('📥 Backend response status:', response.status);

            if (response.ok) {
                const responseData = await response.json();
                console.log('✅ Setup completed successfully:', responseData);
                // Ensure auth context has latest profile to avoid redirect loop
                await refreshUserProfile();
                toast.success('🎉 Setup completed successfully! Welcome to WTW!');
                navigate('/', { replace: true });
            } else {
                const errorData = await response.json();
                console.error('❌ Backend error:', errorData);
                throw new Error(`Backend error: ${errorData.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('❌ Error saving profile:', error);
            toast.error(`Setup failed: ${error.message}. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    // Step components
    const renderStep1 = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
        >
            <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <User className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome to WTW! 👋</h2>
                <p className="text-gray-600 text-lg">Let's personalize your experience</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        What should we call you?
                    </label>
                    <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => handleInputChange('display_name', e.target.value)}
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors"
                        placeholder="Enter your display name..."
                    />
                </div>
            </div>
        </motion.div>
    );

    const renderStep2 = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
        >
            <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">What do you love to do? ✨</h2>
                <p className="text-gray-600 text-lg">Select activities that spark your interest</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {activityOptions.map((activity) => (
                        <label key={activity.name} className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                            formData.activityPreferences.includes(activity.name)
                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}>
                            <input
                                type="checkbox"
                                checked={formData.activityPreferences.includes(activity.name)}
                                onChange={() => handleArrayToggle('activityPreferences', activity.name)}
                                className="sr-only"
                            />
                            <span className="text-2xl mb-2">{activity.icon}</span>
                            <span className="text-sm font-medium text-gray-700 text-center">{activity.name}</span>
                            {formData.activityPreferences.includes(activity.name) && (
                                <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-blue-500" />
                            )}
                        </label>
                    ))}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Other activities (optional)
                    </label>
                    <input
                        type="text"
                        value={formData.customActivities}
                        onChange={(e) => handleInputChange('customActivities', e.target.value)}
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors"
                        placeholder="Add your own activities..."
                    />
                </div>
            </div>
        </motion.div>
    );

    const renderStep3 = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
        >
            <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Dietary Preferences 🍽️</h2>
                <p className="text-gray-600 text-lg">Help us find the perfect dining options for you</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Dietary Restrictions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {dietaryOptions.map((option) => (
                            <label key={option.name} className={`relative flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.dietaryRestrictions.includes(option.name)
                                    ? 'border-orange-500 bg-orange-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={formData.dietaryRestrictions.includes(option.name)}
                                    onChange={() => handleArrayToggle('dietaryRestrictions', option.name)}
                                    className="sr-only"
                                />
                                <span className="text-lg mr-3">{option.icon}</span>
                                <span className="text-sm font-medium text-gray-700">{option.name}</span>
                                {formData.dietaryRestrictions.includes(option.name) && (
                                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-orange-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Other dietary needs (optional)
                    </label>
                    <input
                        type="text"
                        value={formData.customDietary}
                        onChange={(e) => handleInputChange('customDietary', e.target.value)}
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg transition-colors"
                        placeholder="Add other dietary requirements..."
                    />
                </div>
            </div>
        </motion.div>
    );

    const renderStep4 = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
        >
            <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Location & Budget 📍</h2>
                <p className="text-gray-600 text-lg">Set your travel and spending preferences</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Travel Radius</h3>
                    <div className="space-y-3">
                        {radiusOptions.map((option) => (
                            <label key={option.value} className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.travelRadius === option.value
                                    ? 'border-green-500 bg-green-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="radio"
                                    name="travelRadius"
                                    value={option.value}
                                    checked={formData.travelRadius === option.value}
                                    onChange={(e) => handleInputChange('travelRadius', e.target.value)}
                                    className="sr-only"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-700">{option.label}</div>
                                    <div className="text-xs text-gray-500">{option.description}</div>
                                </div>
                                {formData.travelRadius === option.value && (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Budget Range</h3>
                    <div className="space-y-3">
                        {budgetOptions.map((option) => (
                            <label key={option.value} className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.budgetRange === option.value
                                    ? 'border-green-500 bg-green-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="radio"
                                    name="budgetRange"
                                    value={option.value}
                                    checked={formData.budgetRange === option.value}
                                    onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                                    className="sr-only"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-700">{option.label}</div>
                                    <div className="text-xs text-gray-500">{option.description}</div>
                                </div>
                                {formData.budgetRange === option.value && (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );

    const renderStep5 = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
        >
            <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Time & Group Preferences ⏰</h2>
                <p className="text-gray-600 text-lg">Final touches for your perfect plans</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Preferred Times</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {timeOptions.map((time) => (
                            <label key={time.name} className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.preferredTimes.includes(time.name)
                                    ? 'border-purple-500 bg-purple-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={formData.preferredTimes.includes(time.name)}
                                    onChange={() => handleArrayToggle('preferredTimes', time.name)}
                                    className="sr-only"
                                />
                                <span className="text-2xl mb-2">{time.icon}</span>
                                <span className="text-sm font-medium text-gray-700 text-center">{time.name}</span>
                                <span className="text-xs text-gray-500">{time.time}</span>
                                {formData.preferredTimes.includes(time.name) && (
                                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-purple-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Day Preferences</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {dayOptions.map((day) => (
                            <label key={day.name} className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.dayPreferences.includes(day.name)
                                    ? 'border-purple-500 bg-purple-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={formData.dayPreferences.includes(day.name)}
                                    onChange={() => handleArrayToggle('dayPreferences', day.name)}
                                    className="sr-only"
                                />
                                <span className="text-lg mb-2">{day.icon}</span>
                                <span className="text-sm font-medium text-gray-700">{day.short}</span>
                                {formData.dayPreferences.includes(day.name) && (
                                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-purple-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Comfortable Group Size</h3>
                    <div className="space-y-3">
                        {groupSizeOptions.map((option) => (
                            <label key={option.value} className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                formData.comfortableGroupSize === option.value
                                    ? 'border-purple-500 bg-purple-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                                <input
                                    type="radio"
                                    name="comfortableGroupSize"
                                    value={option.value}
                                    checked={formData.comfortableGroupSize === option.value}
                                    onChange={(e) => handleInputChange('comfortableGroupSize', e.target.value)}
                                    className="sr-only"
                                />
                                <Users className="w-6 h-6 mr-4 text-purple-500" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-700">{option.label}</div>
                                    <div className="text-xs text-gray-500">{option.description}</div>
                                </div>
                                {formData.comfortableGroupSize === option.value && (
                                    <CheckCircle className="w-5 h-5 text-purple-500" />
                                )}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );

    const renderStep = () => {
        switch (currentStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            case 5: return renderStep5();
            default: return renderStep1();
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        Welcome to WTW! 🎉
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Let's personalize your experience in just a few steps
                    </p>
                </motion.div>

                {/* Progress Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-12"
                >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-sm font-semibold text-gray-700">Step {currentStep} of {totalSteps}</span>
                            <span className="text-sm font-semibold text-blue-600">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <motion.div
                                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="mb-8"
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center"
                >
                    <button
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                        className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                            currentStep === 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                    >
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        <span>Previous</span>
                    </button>

                    {currentStep < totalSteps ? (
                        <button
                            onClick={handleNext}
                            className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-green-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                        >
                            <span>Next</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${
                                loading
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-green-500 to-blue-500 hover:shadow-lg'
                            } text-white`}
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    <span>Complete Setup</span>
                                </>
                            )}
                        </button>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default SetupWizard;
